import { RefObject, useEffect } from 'react';

const PHONE_INPUT_SELECTOR = '.hsfc-PhoneInput > input[data-hsfc-id="TextInput"]';

/**
 * HubSpot prefills the phone field with the country dial code (e.g. "+45"). Tabbing into an input
 * selects its whole value, so the next keystroke wipes the dial code. Collapse the selection to the
 * end instead, leaving the caret ready for the number.
 */
export function useHubSpotPhoneCaret(containerRef: RefObject<HTMLDivElement>, formHtmlCode: string, isMounted: boolean) {
	useEffect(() => {
		if (!containerRef.current || !isMounted) return;

		const container = containerRef.current;

		function moveCaretToEnd(event: FocusEvent) {
			const input = event.target;
			if (!(input instanceof HTMLInputElement) || !input.matches(PHONE_INPUT_SELECTOR)) return;

			// Run after the browser has applied its own selection, and only when it selected the whole
			// value, so clicking into the middle of an existing number still places the caret there.
			requestAnimationFrame(() => {
				if (document.activeElement !== input) return;

				const end = input.value.length;
				if (end === 0 || input.selectionStart !== 0 || input.selectionEnd !== end) return;

				try {
					input.setSelectionRange(end, end);
				} catch (error) {
					console.warn('Failed to place phone input caret:', error);
				}
			});
		}

		container.addEventListener('focusin', moveCaretToEnd);
		return () => container.removeEventListener('focusin', moveCaretToEnd);
	}, [formHtmlCode, isMounted]);
}
